// @ts-nocheck
import { useMemo } from 'react';
import type { Analysis } from '../../../services/AnalysisApiClient';
import { CLOSING_DATE_COLORS, closingDateVariant } from '../../../utils/closingDate';
import { useClientWorkspaceCompanyCard } from './useClientWorkspaceCompanyCard';

export type ClientWorkflowSummaryBadge = {
  label: string;
  value: string;
  valueClass?: string;
};

function formatDealPriceBadge(dealPriceVal: unknown): string {
  if (dealPriceVal == null || dealPriceVal === '') return '—';
  if (typeof dealPriceVal === 'number') {
    return dealPriceVal >= 1e6 ? `$${(dealPriceVal / 1e6).toFixed(0)}M` : `$${dealPriceVal.toLocaleString()}`;
  }
  const n = Number(dealPriceVal);
  return n >= 1e6 ? `$${(n / 1e6).toFixed(0)}M` : `$${n.toLocaleString()}`;
}

/**
 * Workflow overview + run history summary chips (closing left of disqualified-individuals count).
 */
export function useClientWorkflowSummaryBadges(
  formData: {
    companyName?: string;
    closingDate?: string;
    dealPrice?: unknown;
    individuals?: unknown[];
  },
  archivedAnalyses?: Analysis[]
): ClientWorkflowSummaryBadge[] {
  const { showDealContextCard } = useClientWorkspaceCompanyCard(archivedAnalyses);
  const individuals = formData.individuals || [];

  const closingRaw = String(formData.closingDate ?? '').trim();
  const closingStr = closingRaw || '—';
  const closingValueClass = closingRaw ? CLOSING_DATE_COLORS[closingDateVariant(closingRaw)] : 'text-gray-600';
  const dealPriceStr = formatDealPriceBadge(formData.dealPrice);

  return useMemo(() => {
    const rows: ClientWorkflowSummaryBadge[] = [
      { label: 'Company', value: formData.companyName || '—' },
      { label: 'Closing', value: closingStr, valueClass: closingValueClass },
      { label: 'Disqualified Individuals', value: String(individuals.length) },
      { label: 'Deal Price', value: dealPriceStr },
    ];
    /** Shell header already shows company for linked clients — closing sits left of the DI badge here. */
    if (showDealContextCard) {
      return rows.filter((r) => r.label !== 'Company');
    }
    return rows;
  }, [showDealContextCard, formData.companyName, closingStr, closingValueClass, individuals.length, dealPriceStr]);
}
